import { inject, watch } from "aurelia";
import { IEventAggregator } from 'aurelia';

import { IZeusClient, ZeusClient } from '@services/graphql-service/zeus-client';
import { IAuthService } from "@interfaces/auth-service/auth-service-interface";
import { ModelTypes } from "@zeus";
import { IDialogService } from "@aurelia/dialog";
import Shepherd from "shepherd.js";

@inject(IZeusClient, IEventAggregator, IAuthService, IDialogService)
export class HomePage {
  stepsToShow: string[] = ['brandkit', 'site', 'posts', 'blog', 'products']
  private homeOnboardCurrentStep: number
  private isHomeOnboardCompleted: boolean
  timeout: NodeJS.Timeout
  private tour: Shepherd.Tour;
  private initialOnboard: Shepherd.Tour;
  constructor(
    private zeusClient: ZeusClient,
    readonly ea: IEventAggregator,
    readonly auth: IAuthService,
    readonly dialogService: IDialogService,
  ) {
    this.tour = new Shepherd.Tour({
      defaultStepOptions: {
        scrollTo: true,
        cancelIcon: {
          enabled: true
        },
        modalOverlayOpeningPadding: 5,
      },
      useModalOverlay: true,
    });

    this.tour.on('show', async (event) => {
      const currentIndex = this.tour.steps.indexOf(event.step);
      const isLastStep = currentIndex === (this.tour.steps.length - 1);

      await this.saveOnboardStep(currentIndex, isLastStep)
    });

  }

  startTour() {
    this.initialOnboard = new Shepherd.Tour({
      defaultStepOptions: {
        scrollTo: false,
        cancelIcon: { enabled: true },
      },
      useModalOverlay: false
    });

    this.initialOnboard.addStep({
      title: 'A magia começou!',
      classes: 'copymagico-popover static-popover',
      text: 'Vamos te apresentar nossa ferramenta em alguns passos rápidos. Está pronto para começar?',
      buttons: [
        {
          text: '<div class="icon-box-md -mr-2"><i hidden.class="isLoading" class="mdi mdi-arrow-right-circle-outline"></i></div> <span>Começar agora</span>',
          classes: 'shepherd-btn btn-right-icon',
          action: () => {
            this.initialOnboard.complete();
            this.startMainTour();
          }
        }
      ],
    });

    this.initialOnboard.start();
  }

  private startMainTour() {
    this.tour.addStep({
      id: 'first-step',
      classes: `copymagico-popover ${window.innerWidth < 768 ? 'static-popover' : ''}`,
      text: 'Temos muitas funcionalidades e tudo é acessível por esse menu.',
      title: 'Se encontre aqui',
      attachTo: {
        element: '#sidebar-navigation',
        on: 'right-start',
      },
      buttons: [
        {
          text: '<div class="icon-box-md -mr-2"><i hidden.class="isLoading" class="mdi mdi-arrow-right-circle-outline"></i></div> <span>Próximo</span>',
          classes: 'shepherd-btn btn-right-icon',
          action: () => {
            this.tour.next()
          }
        }
      ],
      when: {
        show: () => {
          const sidebar = document.getElementById('openAndCloseSidebar');
          if (sidebar) {
            if (localStorage.getItem('rankmyseite-drawer-open') == 'false') {
              sidebar.click();
            }
          }
        },
        hide: () => {
          const sidebar = document.getElementById('openAndCloseSidebar');
          if (sidebar) {
            if (localStorage.getItem('rankmyseite-drawer-open') == 'true') {
              sidebar.click();
            }
          }
        }
      }
    });

    this.tour.addStep({
      id: 'second-step',
      classes: `copymagico-popover ${window.innerWidth < 768 ? 'static-popover' : ''}`,
      title: 'Perfil e opções',
      text: 'Aqui você encontra seus dados, as configurações e irá gerenciar sua assinatura com a Copymagico!',
      attachTo: {
        element: '#dropdownUserMenu',
        on: 'left-start'
      },
      buttons: [
        {
          text: '<div class="icon-box-md"><i class="mdi mdi-arrow-left-circle-outline"></i></div>',
          classes: 'shepherd-btn btn-left-icon',
          action: () => this.tour.back()
        },
        {
          text: '<div class="icon-box-md -mr-2"><i hidden.class="isLoading" class="mdi mdi-arrow-right-circle-outline"></i></div> <span>Próximo</span>',
          classes: 'shepherd-btn btn-right-icon',
          action: () => this.tour.next()
        }
      ],
      when: {
        show: () => {
          const dropdown = document.getElementById('dropdownUserMenuButton');
          if (dropdown) {
            dropdown.click();
          }
        }
      }
    });

    this.tour.addStep({
      id: 'third-step',
      classes: `copymagico-popover ${window.innerWidth < 768 ? 'static-popover' : ''}`,
      title: 'O que fazer primeiro?',
      text: 'Aqui vamos recomendar ações úteis baseados nos seus objetivos.',
      attachTo: {
        element: '#next-steps',
        on: 'left-start'
      },
      buttons: [
        {
          text: '<div class="icon-box-md"><i class="mdi mdi-arrow-left-circle-outline"></i></div>',
          classes: 'shepherd-btn btn-left-icon',
          action: () => this.tour.back()
        },
        {
          text: '<div class="icon-box-md -mr-2"><i hidden.class="isLoading" class="mdi mdi-arrow-right-circle-outline"></i></div> <span>Próximo</span>',
          classes: 'shepherd-btn btn-right-icon',
          action: this.tour.next
        }
      ]
    });

    this.tour.addStep({
      id: 'fourth-step',
      classes: `copymagico-popover ${window.innerWidth < 768 ? 'static-popover' : ''}`,
      title: 'A magia começou!',
      text: 'Vamos começar definindo sua marca para tornar seus conteúdos personalizados e eficientes.',
      attachTo: {
        element: '#brandkit',
        on: 'bottom'
      },
      buttons: [
        {
          text: '<div class="icon-box-md"><i class="mdi mdi-arrow-left-circle-outline"></i></div>',
          classes: 'shepherd-btn btn-left-icon',
          action: this.tour.back
        },
        {
          text: 'Finalizar',
          classes: 'shepherd-btn btn-right-icon',
          action: this.tour.complete
        }
      ]
    });

    this.tour.start();
  }

  async saveOnboardStep(currentStep: number, isLastStep: boolean) {

    await this.zeusClient.chain('mutation')({
      update_userOnboarding_by_pk: [
        {
          pk_columns: { userId: this.auth.getUser().id },
          _set: {
            homeOnboardCurrentStep: currentStep,
            isHomeOnboardCompleted: isLastStep,
          }
        },
        {
          homeOnboardCurrentStep: true,
          isHomeOnboardCompleted: true,
        }
      ]
    })
  }

  async attached() {

    const response = await this.zeusClient.chain('query')({
      userOnboarding_by_pk: [
        {
          userId: this.auth.getUser().id
        },
        {
          homeOnboardCurrentStep: true,
          isHomeOnboardCompleted: true,
        }
      ],
      brands: [
        { limit: 1 },
        {
          id: true,
          currentStep: true,
        }
      ]
    })

    if (response?.brands?.length) {
      this.stepsToShow = (response?.brands[0]?.id && response?.brands[0]?.currentStep < 3) || !response?.brands[0]?.id ? ['brandkit', 'site', 'posts', 'blog', 'products'] : ['site', 'posts', 'blog', 'products'];
    }

    this.homeOnboardCurrentStep = response?.userOnboarding_by_pk?.homeOnboardCurrentStep as number
    this.isHomeOnboardCompleted = response?.userOnboarding_by_pk?.isHomeOnboardCompleted

    const modal = document.getElementById('user-onboarding-modal')

    if (this.zeusClient?.userDetails?.onboardCompleted === true && !this.isHomeOnboardCompleted && !modal) {
      this.onboardStart()
    }
  }

  @watch('zeusClient.userDetails')
  async handleUserDetailsSubscription(newValue: ModelTypes['userMetadata']) {
    if (typeof newValue?.onboardCompleted === 'undefined') return;

    const modal = document.getElementById('user-onboarding-modal')

    if (newValue?.onboardCompleted === true && !this.isHomeOnboardCompleted && !modal) {
      this.onboardStart()
    }
  }

  onboardStart() {
    if (this.timeout) return

    this.timeout = setTimeout(() => {
      console.log('watch', {
        onboardCompleted: this.zeusClient?.userDetails?.onboardCompleted,
        isHomeOnboardCompleted: this.isHomeOnboardCompleted,
        modal: document.getElementById('user-onboarding-modal'),
      })
      this.startTour();
    }, 5000)
  }

  detaching() {
    clearTimeout(this.timeout);

    this.initialOnboard?.cancel();
    this.tour?.cancel();
  }
}